// NOTE: First-pass UK GDPR Article 28 Data Processing Agreement template for the
// TS1 monitoring platform. Not yet reviewed by a solicitor. Customers may request
// a countersigned PDF version; the contents here describe the standard terms we
// offer. Schedule a solicitor review before relying on this in a dispute.

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const lastUpdated = '11 May 2026';

const sections = [
  {
    id: 'introduction',
    title: '1. Introduction',
    body: (
      <>
        <p>
          This Data Processing Agreement (&quot;DPA&quot;) forms part of the agreement between
          <strong> Thermal Systems Ltd</strong> (the &quot;Processor&quot;, &quot;we&quot;, &quot;our&quot;,
          or &quot;us&quot;) and the customer organisation (the &quot;Controller&quot; or &quot;you&quot;)
          for the TS1 monitoring platform and related services (the &quot;Service&quot;). It sets out how we
          process personal data on your behalf in compliance with the UK GDPR, the Data Protection Act 2018,
          and any successor or equivalent law (&quot;Data Protection Law&quot;).
        </p>
        <p className="mt-3">
          Where this DPA and the{' '}
          <Link to="/terms" className="text-indigo-600 dark:text-indigo-400 hover:underline">
            Terms of Service
          </Link>{' '}
          conflict in relation to the processing of personal data, this DPA prevails.
        </p>
      </>
    ),
  },
  {
    id: 'roles',
    title: '2. Roles of the parties',
    body: (
      <p>
        In relation to personal data processed in the Service on your behalf, you are the data controller and
        we are the data processor. Each party will comply with its respective obligations under Data
        Protection Law.
      </p>
    ),
  },
  {
    id: 'processing-details',
    title: '3. Details of processing',
    body: (
      <>
        <p className="font-semibold">Subject matter</p>
        <p className="mt-1">
          Provision of the TS1 cloud-hosted monitoring service, including ingestion of sensor data from your
          base units, display of readings and alarms in the dashboard and mobile applications, and delivery
          of notifications.
        </p>
        <p className="mt-3 font-semibold">Duration</p>
        <p className="mt-1">
          For as long as you use the Service or hold an account, plus any post-termination retention period
          set out in section 11.
        </p>
        <p className="mt-3 font-semibold">Nature and purpose</p>
        <p className="mt-1">
          Hosting, storing, transmitting, authenticating, logging, and otherwise processing personal data to
          deliver and secure the Service and to communicate with users on your behalf.
        </p>
        <p className="mt-3 font-semibold">Categories of data subjects</p>
        <ul className="list-disc pl-6 space-y-1 mt-1">
          <li>Your employees, contractors, and other authorised personnel who use the Service.</li>
          <li>
            Recipients you designate to receive alarm or status notifications (for example, on-call staff).
          </li>
          <li>Administrators of your organisation who configure the Service.</li>
        </ul>
        <p className="mt-3 font-semibold">Categories of personal data</p>
        <ul className="list-disc pl-6 space-y-1 mt-1">
          <li>Identity and contact data: names, email addresses, telephone numbers, job roles.</li>
          <li>
            Authentication and security data: hashed credentials, biometric credential identifiers, session
            tokens, IP addresses, user-agent strings, login and audit timestamps.
          </li>
          <li>
            Usage data: actions performed in the Service, configuration changes, alarms acknowledged, reports
            generated.
          </li>
          <li>
            Any other personal data you choose to submit to the Service through user profiles, sensor names,
            zone names, comments, or notification recipient lists.
          </li>
        </ul>
        <p className="mt-3 font-semibold">Special category data</p>
        <p className="mt-1">
          The Service is not intended for the storage of special category personal data (Article 9 UK GDPR)
          or criminal-offence data. You must not deliberately submit such data to the Service except where
          incidental to ordinary identity and contact data.
        </p>
      </>
    ),
  },
  {
    id: 'processor-obligations',
    title: '4. Our obligations as Processor',
    body: (
      <ul className="list-disc pl-6 space-y-2">
        <li>
          Process personal data only on your documented instructions, including those set out in the Terms
          of Service and your configuration of the Service, except where required to do so by UK or EU law
          (in which case we will inform you unless the law prohibits this on important grounds of public
          interest).
        </li>
        <li>
          Ensure that persons authorised to process personal data are bound by appropriate confidentiality
          obligations.
        </li>
        <li>
          Implement appropriate technical and organisational measures designed to protect personal data, as
          described in section 7.
        </li>
        <li>
          Assist you, taking into account the nature of the processing and the information available to us,
          in responding to data subject requests and in meeting your obligations under Articles 32–36 UK
          GDPR (security, breach notification, data protection impact assessments, prior consultation).
        </li>
        <li>
          Make available to you the information reasonably necessary to demonstrate compliance with this
          DPA and Article 28 UK GDPR, and allow for and contribute to audits as set out in section 9.
        </li>
        <li>
          Inform you without undue delay if, in our opinion, an instruction from you infringes Data
          Protection Law.
        </li>
      </ul>
    ),
  },
  {
    id: 'controller-obligations',
    title: '5. Your obligations as Controller',
    body: (
      <ul className="list-disc pl-6 space-y-2">
        <li>
          Ensure that you have a lawful basis under Data Protection Law to process the personal data you
          submit to the Service and to instruct us to process it on your behalf.
        </li>
        <li>
          Provide all required privacy notices to your data subjects, including those describing our role as
          your processor.
        </li>
        <li>
          Configure the Service appropriately for your environment (including user permissions, notification
          recipients, and retention settings) and keep that configuration up to date.
        </li>
        <li>
          Not submit special category data, criminal-offence data, payment card data, or any personal data
          relating to children to the Service except as set out in section 3.
        </li>
      </ul>
    ),
  },
  {
    id: 'subprocessors',
    title: '6. Sub-processors',
    body: (
      <>
        <p>
          You provide general written authorisation for us to engage sub-processors to assist in providing
          the Service. We will impose, by written contract, data protection obligations on each
          sub-processor that are no less protective than those in this DPA. We remain responsible to you for
          the performance of any sub-processor we engage.
        </p>
        <p className="mt-3">
          Our current sub-processors include the categories listed below. The specific providers we use may
          change from time to time; the current list is maintained internally and is available on request.
        </p>
        <ul className="list-disc pl-6 space-y-2 mt-3">
          <li>
            <strong>Cloud platform and edge networking</strong> (dashboard hosting, authentication, API
            workers).
          </li>
          <li>
            <strong>Managed database and storage</strong> (per-tenant database schemas, file storage).
          </li>
          <li>
            <strong>Container hosting</strong> (always-on services such as the data ingestion pipeline).
          </li>
          <li>
            <strong>SMS gateway</strong> for delivering alarm and notification messages.
          </li>
          <li>
            <strong>Transactional email provider</strong> for delivering alarm, account, and notification
            emails.
          </li>
          <li>
            <strong>Error monitoring and operational logging</strong> to detect and respond to faults in the
            Service.
          </li>
        </ul>
        <p className="mt-3">
          We will give you reasonable advance notice of any intended addition or replacement of a
          sub-processor that materially affects the processing of your personal data. If you object on
          reasonable data-protection grounds, the parties will work in good faith to address the objection;
          if no resolution is reached, you may terminate the affected portion of the Service.
        </p>
      </>
    ),
  },
  {
    id: 'security',
    title: '7. Security measures',
    body: (
      <>
        <p>
          We implement and maintain appropriate technical and organisational measures designed to protect
          personal data against accidental or unlawful destruction, loss, alteration, unauthorised disclosure
          of, or access to personal data transmitted, stored, or otherwise processed in the Service. Current
          measures include, at a minimum:
        </p>
        <ul className="list-disc pl-6 space-y-2 mt-3">
          <li>
            Encryption of data in transit using current industry-standard TLS, and encryption at rest at the
            storage and database layers.
          </li>
          <li>
            Multi-tenant logical isolation, with each customer&apos;s operational data held in a separate
            database schema.
          </li>
          <li>
            Authentication based on signed JWTs, httpOnly secure cookies, and optional biometric/WebAuthn
            sign-in, with planned multi-factor authentication.
          </li>
          <li>Role-based access control over administrative and operational functions.</li>
          <li>Centralised audit logging of user and administrative actions.</li>
          <li>
            Regular backups, security updates to dependencies, and monitoring of operational health and
            error events.
          </li>
          <li>
            Hosting on infrastructure providers that maintain recognised security certifications (including
            ISO 27001 and SOC 2 where offered by the relevant provider).
          </li>
          <li>
            Principle of least privilege for staff access to production systems, with access reviewed
            periodically.
          </li>
        </ul>
        <p className="mt-3">
          These measures will evolve over time in line with the state of the art and the level of risk to
          data subjects.
        </p>
      </>
    ),
  },
  {
    id: 'breach',
    title: '8. Personal data breach notification',
    body: (
      <p>
        We will notify you without undue delay, and in any event within 72 hours of becoming aware, of any
        personal data breach affecting personal data processed under this DPA. Our notification will include
        the information reasonably available to us at that time and will be updated as more information
        becomes available. We will reasonably assist you in meeting your own breach-notification and
        communication obligations under Articles 33 and 34 UK GDPR.
      </p>
    ),
  },
  {
    id: 'audit',
    title: '9. Information and audits',
    body: (
      <p>
        We will make available to you, on reasonable written request, the information reasonably necessary to
        demonstrate compliance with this DPA. Where you reasonably require an audit, we will work with you
        to agree a mutually acceptable scope, schedule, and confidentiality terms, and may satisfy your
        audit rights through third-party audit reports, certifications, or written summaries of our
        controls. Audits will be conducted in a manner that does not unduly disrupt the Service or
        compromise the security of other customers&apos; data. You will bear your own costs of any audit and
        our reasonable costs of cooperation.
      </p>
    ),
  },
  {
    id: 'transfers',
    title: '10. International transfers',
    body: (
      <p>
        If we transfer personal data outside the UK or, where applicable, the EEA, we will do so only where
        a valid transfer mechanism is in place under Data Protection Law (for example, an adequacy decision
        or the UK International Data Transfer Agreement / Addendum to the EU Standard Contractual Clauses).
        You authorise us to enter into appropriate transfer mechanisms with sub-processors on your behalf.
      </p>
    ),
  },
  {
    id: 'return-deletion',
    title: '11. Return or deletion on termination',
    body: (
      <p>
        On termination of the Service, we will, at your written choice, return personal data to you in a
        commonly used format, or delete (or anonymise) personal data within a reasonable period unless we
        are required by UK or EU law to retain it. Backups containing personal data may be retained for the
        rotation period of those backups and will be subject to ongoing security measures until deleted in
        the ordinary course.
      </p>
    ),
  },
  {
    id: 'liability',
    title: '12. Liability',
    body: (
      <p>
        Each party&apos;s liability under or in connection with this DPA is subject to the limitations and
        exclusions of liability set out in the{' '}
        <Link to="/terms" className="text-indigo-600 dark:text-indigo-400 hover:underline">
          Terms of Service
        </Link>
        , except to the extent that those limitations or exclusions are not permitted by Data Protection Law.
      </p>
    ),
  },
  {
    id: 'general',
    title: '13. General',
    body: (
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Governing law.</strong> This DPA is governed by the laws of England and Wales and is
          subject to the exclusive jurisdiction of the courts of England and Wales, save as otherwise
          required by Data Protection Law.
        </li>
        <li>
          <strong>Order of precedence.</strong> In the event of any conflict between this DPA and any other
          document forming part of the agreement between the parties, in matters relating to the processing
          of personal data this DPA prevails.
        </li>
        <li>
          <strong>Updates.</strong> We may update this DPA from time to time to reflect changes in Data
          Protection Law, sub-processors, or operational practice. Material changes will be notified to
          active customers.
        </li>
        <li>
          <strong>Counterpart copy.</strong> A countersigned PDF copy of this DPA can be provided on request
          for customers whose own compliance processes require one.
        </li>
      </ul>
    ),
  },
  {
    id: 'contact',
    title: '14. Contact',
    body: (
      <p>
        Data-protection enquiries can be sent to Thermal Systems Ltd via our website{' '}
        <a
          href="https://thermal-systems.co.uk"
          target="_blank"
          rel="noopener noreferrer"
          className="text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          thermal-systems.co.uk
        </a>
        , by telephone on{' '}
        <a href="tel:02034881030" className="text-indigo-600 dark:text-indigo-400 hover:underline">
          020 3488 1030
        </a>
        , or through the contact channels your organisation has been given for support.
      </p>
    ),
  },
];

export default function DataProcessingAgreementPage() {
  return (
    <div className="min-h-screen bg-app-canvas dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <Link
          to="/login"
          className="inline-flex items-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to sign in
        </Link>

        <header className="mb-10 pb-8 border-b border-gray-200 dark:border-gray-700">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
            Data Processing Agreement
          </h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Last updated: {lastUpdated}</p>
          <p className="mt-4 text-gray-600 dark:text-gray-300">
            This DPA describes the standard data-processing terms on which Thermal Systems Ltd processes
            personal data on your behalf in connection with the TS1 monitoring platform.
          </p>
        </header>

        <nav className="mb-10 p-4 rounded-lg bg-app-panel dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Contents</h2>
          <ul className="text-sm space-y-1 text-indigo-600 dark:text-indigo-400">
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="hover:underline">
                  {s.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="space-y-10 text-gray-700 dark:text-gray-300 text-sm sm:text-base leading-relaxed">
          {sections.map((s) => (
            <section key={s.id} id={s.id}>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">{s.title}</h2>
              {s.body}
            </section>
          ))}
        </div>

        <footer className="mt-14 pt-8 border-t border-gray-200 dark:border-gray-700 text-center text-sm text-gray-500 dark:text-gray-400">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mb-3">
            <Link to="/terms" className="hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline">
              Terms of Service
            </Link>
            <Link to="/compliance" className="hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline">
              Security &amp; Compliance
            </Link>
            <Link to="/privacy" className="hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline">
              Privacy Policy
            </Link>
            <Link to="/cookies" className="hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline">
              Cookie Policy
            </Link>
          </div>
          <p>© {new Date().getFullYear()} Thermal Systems Ltd. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}
